import { computed, ref } from 'vue';
import { useRealtimeKitAudio } from 'shared/composables/useRealtimeKitAudio';
import { useAgoraAudio } from 'shared/composables/useAgoraAudio';

export const WEB_CALL_ENGINES = {
  AGORA: 'agora',
  REALTIME_KIT: 'realtimekit',
};

/**
 * Customer-side audio for a web call, on whichever engine the server created
 * the call with. The engine arrives on the `connection` payload returned by the
 * join/create endpoints (see Voice::Web::TokenService) — a call is never
 * joinable on an engine other than the one it was set up on, so this only ever
 * picks once per call.
 *
 * Exposes the same surface as the two engine composables it wraps, so consumers
 * read `state`/`muted`/`remotePeerName` without knowing which one is live.
 */
export const useWebCallAudio = () => {
  const realtimeKit = useRealtimeKitAudio();
  const agora = useAgoraAudio();
  const engine = ref(WEB_CALL_ENGINES.REALTIME_KIT);

  const active = () =>
    engine.value === WEB_CALL_ENGINES.AGORA ? agora : realtimeKit;

  const connect = async connection => {
    engine.value =
      connection?.engine === WEB_CALL_ENGINES.AGORA
        ? WEB_CALL_ENGINES.AGORA
        : WEB_CALL_ENGINES.REALTIME_KIT;
    // RealtimeKit encodes the room and identity inside the token, so it takes
    // the bare string; Agora needs the channel and uid alongside it.
    if (engine.value === WEB_CALL_ENGINES.AGORA) {
      await agora.connect(connection);
      return;
    }
    await realtimeKit.connect(connection.token);
  };

  return {
    state: computed(() => active().state.value),
    errorMessage: computed(() => active().errorMessage.value),
    muted: computed(() => active().muted.value),
    remotePeerName: computed(() => active().remotePeerName.value),
    connect,
    disconnect: () => active().disconnect(),
    toggleMute: () => active().toggleMute(),
  };
};
